import {
  faCheckSquare,
  faSquare,
  faSquareMinus
} from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Autocomplete,
  Checkbox,
  Chip,
  FormControl,
  Grid,
  InputLabel,
  TextField
} from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';

import type {
  AlertDeviceField,
  ConnectionInputSchema
} from '../../SourceCreate.types';
import type { AlertDeviceFieldsSelectorProps } from './AlertDeviceFieldsSelector.types';

const SELECT_ALL_OPTION = '__SELECT_ALL__';

/**
 * AlertDeviceFieldsSelector renders a multi-select autocomplete for Medigate device alert fields.
 * Allows selection of which device alert types to ingest from Medigate sources.
 */
const AlertDeviceFieldsSelector = ({
  connectionInputSchema,
  deviceAlertFields,
  onDeviceAlertFieldsChange,
  isResettingSourceType
}: AlertDeviceFieldsSelectorProps) => {
  const { control, formState } = useFormContext();
  const { errors } = formState;

  if (isResettingSourceType) return null;

  const hasError = Boolean(errors.device_alert_fields);
  const errorMessage = errors.device_alert_fields?.message as string;

  // Only shown for Medigate sources
  if (!connectionInputSchema?.device_alert_fields) return null;

  const realOptions = ((connectionInputSchema as ConnectionInputSchema)
    ?.device_alert_fields?.options ?? []) as unknown as AlertDeviceField[];

  const selectAllOption = { key: SELECT_ALL_OPTION } as AlertDeviceField;
  const allOptions = [selectAllOption, ...realOptions];

  return (
    <Grid size={{ xs: 12, sm: 8, md: 8 }}>
      <InputLabel>
        {connectionInputSchema?.device_alert_fields?.label}
        <span> *</span>
      </InputLabel>
      <FormControl fullWidth>
        <Controller
          control={control}
          name={connectionInputSchema?.device_alert_fields?.name}
          rules={{
            required: 'Device Alert Fields is Required'
          }}
          render={({ field: { onChange: fieldOnChange } }) => {
            const selectedValues = deviceAlertFields || [];
            const allSelected =
              realOptions.length > 0 &&
              realOptions.every((opt) =>
                selectedValues.some((val) => val.key === opt.key)
              );
            const someSelected = selectedValues.length > 0 && !allSelected;

            const handleChange = (_: unknown, newValue: AlertDeviceField[]) => {
              const hasSelectAll = newValue.some(
                (v) => v.key === SELECT_ALL_OPTION
              );

              if (hasSelectAll) {
                if (allSelected) {
                  fieldOnChange([]);
                  onDeviceAlertFieldsChange([]);
                } else {
                  fieldOnChange(realOptions);
                  onDeviceAlertFieldsChange(realOptions);
                }
              } else {
                const filtered = newValue.filter(
                  (v) => v.key !== SELECT_ALL_OPTION
                );
                fieldOnChange(filtered);
                onDeviceAlertFieldsChange(filtered);
              }
            };

            return (
              <Autocomplete
                multiple
                limitTags={3}
                value={selectedValues}
                disableCloseOnSelect
                options={allOptions}
                getOptionLabel={(option) =>
                  typeof option === 'string' ? option : option?.key || ''
                }
                onChange={handleChange}
                renderOption={(props, option) => {
                  if (option.key === SELECT_ALL_OPTION) {
                    return (
                      <li {...props}>
                        <Checkbox
                          icon={
                            <FontAwesomeIcon icon={faSquare} fontSize="small" />
                          }
                          checkedIcon={
                            <FontAwesomeIcon
                              icon={faCheckSquare}
                              fontSize="small"
                            />
                          }
                          indeterminateIcon={
                            <FontAwesomeIcon
                              icon={faSquareMinus}
                              fontSize="small"
                            />
                          }
                          style={{ marginRight: 8 }}
                          checked={allSelected}
                          indeterminate={someSelected}
                          disableRipple
                        />
                        Select All
                      </li>
                    );
                  }

                  return (
                    <li {...props}>
                      <strong>{option.key}</strong>
                    </li>
                  );
                }}
                renderValue={(selected, getTagProps) =>
                  selected.map((option, index) => {
                    const { key, ...tagProps } = getTagProps({ index });
                    return (
                      <Chip key={key} {...tagProps} label={`${option.key}`} />
                    );
                  })
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    error={hasError}
                    helperText={errorMessage}
                  />
                )}
                isOptionEqualToValue={(option, value) =>
                  option.key === value.key
                }
              />
            );
          }}
        />
      </FormControl>
    </Grid>
  );
};

export default AlertDeviceFieldsSelector;
